import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import {
  MdAutoAwesome, MdClose, MdSend, MdRefresh, MdOpenInFull,
} from 'react-icons/md';
import { useNavigate } from '../navIntent';
import { SUGGESTIONS } from './match';
import { useMishkahChat, ChatMessage } from './mishkahChat';
import './help.css';
import './assistant.css';

/**
 * «اسأل مشكاة» in the corner: the round button, and the small chat it
 * opens. ⤢ (next to the button and in the chat's head) opens the full
 * page «مشكاة الآلي» with the same conversation (mishkahChat.js).
 * `?` anywhere outside a text field opens it; Esc inside closes it.
 * Not shown on the full page itself.
 */
export default function Assistant({
  user, role, nav, guides, page, open, prefill, onOpen, onClose, onRun,
}) {
  const navigate = useNavigate();
  const {
    chat, busy, send, choose, clear, byId,
  } = useMishkahChat({ user, guides, nav });
  const [text, setText] = useState('');
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const firstName = String(user?.name || '').trim().split(/\s+/)[0] || '';
  const onPage = page === 'mishkah';

  // Opened with a question from elsewhere (the help page's search box).
  useEffect(() => {
    if (open && prefill) setText(prefill);
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open, prefill]);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat, open]);

  useEffect(() => {
    if (onPage) return undefined;
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable;
      if (e.key === '?' && !typing && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        if (open) onClose(); else onOpen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpen, onClose, onPage]);

  if (onPage) return null;

  const ask = (q) => {
    send(q);
    setText('');
    if (inputRef.current) inputRef.current.style.height = '';
  };
  const expand = () => { onClose(); navigate('mishkah'); };
  const handlers = {
    onRun: (id) => { onClose(); onRun(id); },
    onRead: (id) => { onClose(); navigate('help', { guide: id }); },
    onStudent: (studentId) => { onClose(); navigate('student-directory', { studentId }); },
    onChoose: choose,
  };

  if (!open) {
    return (
      <div className="as-dock">
        <button type="button" className="as-fab" onClick={onOpen} data-guide="assistant-fab" title="اسأل مشكاة (?)">
          <MdAutoAwesome size={20} />
          <span>اسأل مشكاة</span>
        </button>
        <button type="button" className="as-fab-expand" onClick={expand} title="مشكاة الآلي — صفحة كاملة" aria-label="مشكاة الآلي" data-guide="assistant-expand">
          <MdOpenInFull size={17} />
        </button>
      </div>
    );
  }

  return (
    <section
      className="as-panel"
      role="dialog"
      aria-label="اسأل مشكاة"
      dir="rtl"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
    >
      <header className="as-head">
        <span className="as-lamp"><MdAutoAwesome size={20} /></span>
        <div className="as-head-text">
          <strong>اسأل مشكاة</strong>
          <span>بيانات المدرسة وطريقة الاستخدام</span>
        </div>
        {chat.length > 0 && (
          <button type="button" className="as-head-btn" onClick={clear} title="محادثة جديدة" aria-label="محادثة جديدة">
            <MdRefresh size={18} />
          </button>
        )}
        <button type="button" className="as-head-btn" onClick={expand} title="مشكاة الآلي — صفحة كاملة" aria-label="صفحة كاملة">
          <MdOpenInFull size={17} />
        </button>
        <button type="button" className="as-head-btn" onClick={onClose} title="إغلاق" aria-label="إغلاق">
          <MdClose size={19} />
        </button>
      </header>

      <div className="as-list" ref={listRef}>
        <div className="as-msg is-bot">
          <p>أهلاً {firstName}! أنا «مشكاة الآلي». اسألني بكلماتك عن بيانات المدرسة، مثل «مين غايب اليوم؟»، أو كيف تعمل شيئاً في النظام، مثل «كيف أضيف شعبة؟». وأتذكّر ما سألتني عنه قبل قليل.</p>
        </div>
        {chat.length === 0 && (
          <div className="as-suggest">
            {(SUGGESTIONS[role] || []).map((s) => <button key={s} type="button" onClick={() => ask(s)}>{s}</button>)}
          </div>
        )}
        {chat.map((m) => <ChatMessage key={m.id} m={m} byId={byId} busy={busy} {...handlers} />)}
      </div>

      <form className="as-form" onSubmit={(e) => { e.preventDefault(); ask(text); }}>
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = `${Math.min(t.scrollHeight, 120)}px`; }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(text); } }}
          placeholder="اكتب سؤالك…"
          rows={1}
          maxLength={600}
          aria-label="سؤالك"
        />
        <button type="submit" className="as-send" disabled={!text.trim() || busy} aria-label="إرسال">
          <MdSend size={19} />
        </button>
      </form>
    </section>
  );
}
